import React, { useContext, useEffect, useState } from "react";
import classNames from "classnames";
import { isMobile } from "react-device-detect";
import { find } from "lodash";
import LegalText from "../../LegalText/LegalText";
import ToggleSwitch from "../../../common/ToggleSwitch";
import { useCustomerAccount } from "../../AccountProvider";
import { getCustomerProfile, updateCustomerProfile } from "../../../../api/custAccountApi";
import { LoadingContext } from "../../LoadingProvider";
import analytics from "../../../../utils/analytics";
import { getErrorCode } from "../../../../utils/errors";
import {Alert, Typography} from "@one-thd/sui-atomic-components";
import {getRole, isB2b2FaEnabled, isUserB2B} from "../../../../utils/index";

const toggleButtonAutomationId = "2fa-toggle-button";
const twoFactorToggleTimeoutMinutes = 10;
const localStorageKeyFor2faToggle = "twoFactorToggleInfo";

/**
 * Check local storage to see if an async 2FA toggle was in process last time user was on the page.
 * If so, compare against the current 2FA status from SVOC to see if process is complete.
 * If not complete, and if the process hasn't timed out, continue polling to check for completion.
 */
const restartPollingIfRequired = (current2faStatus, setAlert, setToggleDisabled, setIsToggleOn, setIs2FA) => {
  try {
    const toggleInfoFromLocalStorage = localStorage.getItem(localStorageKeyFor2faToggle);
    if (toggleInfoFromLocalStorage) {
      const {isOptingIn, startTime} = JSON.parse(toggleInfoFromLocalStorage);
      if (current2faStatus === isOptingIn) {
        setAlert(get2faSuccessAlert(current2faStatus));
        localStorage.removeItem(localStorageKeyFor2faToggle);
      } else {
        // Display the 2FA status that the admin selected
        setIsToggleOn(isOptingIn);
        const timeLeftMilliseconds = (twoFactorToggleTimeoutMinutes * 60000) - (Date.now() - startTime);
        if (timeLeftMilliseconds > 0) {
          pollForTwoFactorToggleCompletion(timeLeftMilliseconds, isOptingIn, setAlert, setToggleDisabled, setIs2FA);
        } else {
          setToggleDisabled(true);
          setAlert(get2faToggleTimeoutAlert(isOptingIn))
          localStorage.removeItem(localStorageKeyFor2faToggle);
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
}

/**
 * For async 2FA toggle, set the selected 2FA status and the start time into local state, so that
 * the state persists even if user leaves the page
 */
const setTwoFactorToggleInfo = (isOptingIn) => {
  const twoFactorToggleInfo = {
    isOptingIn,
    startTime: Date.now()
  }
  localStorage.setItem(localStorageKeyFor2faToggle, JSON.stringify(twoFactorToggleInfo));
}

/**
 * Call the profile endpoint once per minute to determine if async 2FA toggle process has completed.
 * Once timeout is reached or updated 2FA status is seen in profile response, stop polling and
 * clear out the data from local storage.
 */
const pollForTwoFactorToggleCompletion = (pollingMilliseconds, isOptingIn, setAlert, setToggleDisabled, setIs2FA) => {
  let millisecondsPassed = 0;
  const intervalLength = 60000;
  setToggleDisabled(true);
  setAlert(get2faInProgressAlert(isOptingIn));

  const pollingInterval = setInterval(() => {
    const endPolling = () => {
      clearInterval(pollingInterval);
      localStorage.removeItem(localStorageKeyFor2faToggle);
    }
    millisecondsPassed += intervalLength;
    // Make profile call to see if selected 2FA status is reflecting yet
    getCustomerProfile().then(({ response }) => {
      if (response?.data.account.profile.twoFactorAuthOptIn === `${isOptingIn ? "Y" : "N"}`) {
        endPolling();
        setToggleDisabled(false);
        setAlert(get2faSuccessAlert(isOptingIn));
        setIs2FA(isOptingIn);
        if (isOptingIn) {
          analytics.postSettingsSubmissionEvent("security: enable two-factor authentication");
        }
      } else if (millisecondsPassed >= (pollingMilliseconds)) {
        endPolling();
        setAlert(get2faToggleTimeoutAlert(isOptingIn));
      }
    });
  }, intervalLength);
}

// 2FA-related alerts for B2B
const get2faInProgressAlert = (isEnabling) => {
  return {
    alertType: "warning",
    alertContent: <>
      {getNormalSpan(`The ${isEnabling ? "enabling" : "disabling"} of Two-Factor Authentication is in progress. Check back later to confirm it has been ${isEnabling ? "enabled" : "disabled"}.`)}
      {getBoldSpan(" This process may take up to 10 minutes.")}
    </>
  }
}
const get2faToggleTimeoutAlert = (isEnabling) => {
  return {
    alertType: "warning",
    alertContent: getNormalSpan(`The ${isEnabling ? "enabling" : "disabling"} of Two-Factor Authentication is taking longer than expected. Check back later to confirm it has been ${isEnabling ? "enabled" : "disabled"}.`)
  }
}
const get2faSuccessAlert = (isNowEnabled) => {
  return {
    alertType: "success",
    alertContent: getNormalSpan(`Two-Factor Authentication has been successfully turned ${isNowEnabled ? "on" : "off"} for anyone accessing this account.`)
  }
}
const get2faFailureAlert = (wasEnabling) => {
  return {
    alertType: "error",
    alertContent: getNormalSpan(`Two-Factor Authentication could not be ${wasEnabling ? "enabled" : "disabled"} at this time. Please try again later.`)
  }
}
const getNormalSpan = (text) => {
  return <span className="security-page-default-font-size">{text}</span>;
}
const getBoldSpan = (text) => {
  return <span className="security-page-default-font-size u__bold">{text}</span>;
}

const TwoFactorAuth = (props) => {
  const { updateBanner, isUserPasswordlessEnrolled } = props
  const context = useCustomerAccount();
  const [ alertFor2FA, setAlertFor2FA ] = useState(null);
  const { showLoading, hideLoading } = useContext(LoadingContext);
  const isAdmin = getRole() === "ADMIN";
  const isNonAdminB2B = isUserB2B() && !isAdmin;
  const { account } = context;
  const phones = context?.account?.profilePhones?.phone || [];
  const verifiedPhone = find(phones, (ph) => ph?.primaryFlag === "Y" && ph?.phoneVerified === "Y");
  const isVerified = verifiedPhone?.phoneVerified === "Y" || false;
  const { twoFactorAuthOptIn } = context?.account?.profile || {};
  const [ is2FA, setIs2FA ] = useState(twoFactorAuthOptIn === "Y");
  // Maintain the state of the actual toggle switch separately due to async toggle use case
  const [ isToggleOn, setIsToggleOn ] = useState(twoFactorAuthOptIn === "Y");
  // Disable the 2FA toggle for B2C users with no verified phone, and for non-admin B2B users
  const [ isToggleDisabled, setIsToggleDisabled ] = useState((isNonAdminB2B) || !isVerified);

  useEffect(() => {
    if (isUserB2B()) {
      // Check local storage and start polling if enable/disable in progress
      restartPollingIfRequired(is2FA, setAlertFor2FA, setIsToggleDisabled, setIsToggleOn, setIs2FA);
    }
  }, []);

  useEffect(() => {
    // This is for enabling the 2FA toggle if a B2C user verifies a phone number
    setIsToggleDisabled((isNonAdminB2B || !isVerified));
  }, [isVerified]);

  if( (isUserB2B() || isUserPasswordlessEnrolled) && (!isUserB2B() || !isB2b2FaEnabled()) ) {
    return null
  }

  const toggle2FAStatus = () => {
    const accountRequest = {
      ...account,
      profile: {
        ...account.profile,
        twoFactorAuthOptIn: is2FA ? "N" : "Y"
      }
    };
    return updateCustomerProfile(accountRequest);
  };

  const update2FA = () => {
    showLoading();
    toggle2FAStatus().then(({response, error}) => {
      const is2faSelected = !is2FA;
      if (response?.data?.account?.profile && (isUserB2B() || verifiedPhone?.number)) {
        const isNow2FA = response.data.account.profile.twoFactorAuthOptIn === "Y";
        if (isUserB2B() && response?.data?.is2FAUpdateComplete === false) {
          // Set info into local storage (in case user leaves page during process) and start polling
          setTwoFactorToggleInfo(is2faSelected);
          setIsToggleOn(is2faSelected);
          pollForTwoFactorToggleCompletion(twoFactorToggleTimeoutMinutes*60000, is2faSelected, setAlertFor2FA, setIsToggleDisabled, setIs2FA);
        } else {
          if (is2faSelected && isNow2FA) {
            analytics.postSettingsSubmissionEvent("security: enable two-factor authentication");
          }
          if (isUserB2B()) {
            setAlertFor2FA(get2faSuccessAlert(isNow2FA));
          }
          setIs2FA(isNow2FA);
          setIsToggleOn(isNow2FA);
          !isUserB2B() ? updateBanner(isNow2FA, setAlertFor2FA) : null
        }
      } else {
        const errorCode = getErrorCode(error);
        analytics.postErrorEvent("two factor authentication", errorCode);
        setAlertFor2FA(get2faFailureAlert(is2faSelected));
      }
      hideLoading();
    });
  };

  return (
    <>
      <div className="col__12-12 u--paddingNone-horizontal">
        <div className="col__12-12 u--paddingNone u--paddingXsmall-bottom">
          <div className={classNames("col__10-12 u--paddingNone", { "mobile" : isMobile })}>
            <span className="col__12-12 u--paddingNone">
              <Typography weight="bold" variant="body-base">
                Two-Factor Authentication
              </Typography>
              { !is2FA && !isNonAdminB2B &&
                <div className="security-page-sub-header-text">
                  <p className="u--marginXsmall-top">Protect your account by requiring a six-digit code at sign-in{isUserB2B() && " for all users"}.</p>
                  <LegalText/>
                </div>
              }
              { isNonAdminB2B &&
                <div>
                  <p className="u--marginXsmall-top security-page-sub-header-text">This is set by the account administrator.</p>
                </div>
              }
            </span>
          </div>
          <div className="col__2-12 u--paddingNone u--paddingSmall-left">
            <ToggleSwitch id="2FA" onChange={update2FA} disabled={isToggleDisabled} checked={isToggleOn} dataAutomationId={toggleButtonAutomationId} />
          </div>
        </div>
        { alertFor2FA &&
          <div className="col__12-12 u--paddingNone-horizontal">
            <Alert status={alertFor2FA.alertType}>
              {alertFor2FA.alertContent}
            </Alert>
          </div>
        }
      </div>
    </>

  );
};

export default TwoFactorAuth;